import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { AccordionModule } from 'primeng/accordion';
import { CalendarModule } from 'primeng/calendar';
import { CheckboxModule } from 'primeng/checkbox';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { ClientsService } from '../../../core/services/clients/clients.service';
import { ClientsTableComponent } from '../../clients-table/clients-table.component';
import { CommonModule } from '@angular/common';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { AutoAuditDirective } from '../../../shared/directives/auto-audit.directive';
import { AuditService } from '../../../core/services/audit/audit.service';
import { LatestPerFieldMap } from '../../../core/models/audit.model';
import { parseDateOnlyToLocal, toDateOnlyPayload } from '../../../core/utils/date-only.util';

@Component({
  selector: 'app-books-form',
  standalone: true,
  imports: [
    FormsModule, 
    ReactiveFormsModule, 
    DropdownModule, 
    InputTextModule, 
    CheckboxModule, 
    CalendarModule, 
    AccordionModule,
    ClientsTableComponent,
    CommonModule,
    InputTextareaModule,
    AutoAuditDirective,
  ],
  host: {},
  templateUrl: './books-form.component.html',
  styleUrl: './books-form.component.scss'
})
export class BooksFormComponent implements OnChanges {

  allBookTypes = [
    { label: 'INV/CCAA', value: 'INV/CCAA' },
    { label: 'Diario', value: 'DIARIO' },
    { label: 'Libro de actas', value: 'LIBRO_ACTAS', juridicOnly: true },
    { label: 'Libro de socios', value: 'LIBRO_SOCIOS', juridicOnly: true }
  ];

  bookTypes = [];

  configBooksTable: any = {
    headers: [
      { name: 'Tipo', key: 'type' },
      { name: 'Número orden libro', key: 'orderNumber' },
      { name: 'Fecha legalización libro', key: 'bookDate' },
      { name: 'Ejercicio', key: 'fiscalYear' },
      { name: 'Observaciones', key: 'observations' },
      { name: '', key: 'delete' }
    ],
    data: [],
    isBooks: true
  };

  booksForm: FormGroup;
  bookForm: FormGroup;
  booksFiltersForm: FormGroup;

  private booksData: any[] = [];

  isBookModalVisible = false;
  isDeleteBookModalVisible = false;
  editingBookIndex: number | undefined = undefined;
  deleteBookIndex: number;

  @Input() clientId: any;
  @Input() clientType: string;
  @Input() snapshotData: any[] | null = null;
  @Output() formIsDirty = new EventEmitter<boolean>();

  fieldAuditData: LatestPerFieldMap = {};

  constructor(
    private fb: FormBuilder,
    private clientsService: ClientsService,
    private auditService: AuditService,
    ) { 
      this.booksForm = this.fb.group({
        books: this.fb.array([])
      });

      //filters form for the books
      this.booksFiltersForm = this.fb.group({
        type: [''],
        bookDate: [null],
        fiscalYear: ['']
      });

      this.bookForm = this.fb.group({
        id: [],
        type: ['', Validators.required],
        orderNumber: ['', Validators.required],
        bookDate: [null, Validators.required],
        fiscalYear: ['', Validators.required],
        client: [null],
        observations: ['']
      });

      this.booksForm.valueChanges.subscribe(() => {
        if (this.booksForm.dirty) {
          this.formIsDirty.emit(true);
        }
      });

      //subscribe to filters changes
      this.booksFiltersForm.valueChanges.subscribe(() => {
        this.applyBooksFilters();
      });
    }

  async ngOnInit() {
    this.updateBookTypes();
    if (!this.snapshotData) {
      await this.getData();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['snapshotData']) {
      if (this.snapshotData) {
        this.configBooksTable = { ...this.configBooksTable, data: [...this.snapshotData] };
      } else if (!changes['snapshotData'].firstChange) {
        void this.getData();
      }
    }
  }

  updateBookTypes() {
    if (this.clientType === 'JURIDIC_PERSON') {
      this.bookTypes = this.allBookTypes;
    } else {
      this.bookTypes = this.allBookTypes.filter(bookType => !bookType.juridicOnly);
    }
  }

  async getData() {
    if (this.clientId) {
      const booksData = await this.clientsService.getClientBooks(this.clientId);
      this.books.clear();
      booksData.forEach(book => {
        book.bookDate = parseDateOnlyToLocal(book.bookDate);
        this.books.push(this.fb.group({
          type: [book.type, Validators.required],
          orderNumber: [book.orderNumber, Validators.required],
          bookDate: [book.bookDate, Validators.required],
          fiscalYear: [book.fiscalYear, Validators.required],
          id: [book.id],
          client: [book.client],
          observations: [book.observations ?? '']
        }));  
      });
      this.booksData = booksData;
      this.applyBooksFilters();

      try {
        this.fieldAuditData = await this.auditService.getLatestPerField(this.clientId, 'client_book');
      } catch {
        this.fieldAuditData = {};
      }
    }
  }

  private applyBooksFilters() {
    const { type, bookDate, fiscalYear } = this.booksFiltersForm.getRawValue();
    const normalizedFiscalYear = (fiscalYear ?? '').toString().trim().toLowerCase();
    const filterDate = bookDate ? this.toDateKey(bookDate) : null;

    //filter data based on tipe bookdate and fiscal year
    this.configBooksTable.data = (this.booksData || []).filter(book => {
      const matchesType = !type || book.type === type;
      const matchesFiscalYear =
        !normalizedFiscalYear || (book.fiscalYear ?? '').toString().toLowerCase().includes(normalizedFiscalYear);

      const matchesBookDate =
        !filterDate || (book.bookDate ? this.toDateKey(book.bookDate) === filterDate : false);

      return matchesType && matchesBookDate && matchesFiscalYear;
    });
  }

  //helper to convert date to yyyy-mm-dd format
  private toDateKey(dateLike: any): string {
    const date = dateLike instanceof Date ? dateLike : new Date(dateLike);
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  get activeBooksTableConfig(): any {
    if (!this.snapshotData) return this.configBooksTable;
    return {
      ...this.configBooksTable,
      headers: this.configBooksTable.headers.filter(
        (h: any) => h.key !== 'delete',
      ),
    };
  }

  get books(): FormArray {
    return this.booksForm.get('books') as FormArray;
  }

  addBook() {
    const book = this.fb.group({
      type: ['', Validators.required],
      orderNumber: ['', Validators.required],
      bookDate: [null, Validators.required],
      fiscalYear: ['', Validators.required],
      id: [],
      client: [],
      observations: [''],
    });

    this.books.push(book);
  }

  async save() {
    for (let i = 0; i < this.books.length; i++) {
      if (this.books.at(i).dirty) {
        const book = this.books.at(i).value;
        book.client = Number(this.clientId);
        await this.clientsService.saveClientBook({ ...book, bookDate: toDateOnlyPayload(book.bookDate) });
      }
    }
    await this.getData();
  }

  /** Guarda de envío: un doble clic crearía duplicados. */
  isSubmitting = false;
  /** Último error de guardado o borrado de un modal, para mostrarlo al usuario. */
  submitError = '';

  async saveBook() {
    if (this.isSubmitting) return;
    this.isSubmitting = true;
    this.submitError = '';
    try {
      await this.doSaveBook();
    } catch (error) {
      console.error(error);
      this.submitError = 'No se pudo guardar. Revisa los datos e inténtalo de nuevo.';
    } finally {
      this.isSubmitting = false;
    }
  }

  private async doSaveBook() {
    if (this.bookForm.invalid) return;
    const value = this.bookForm.value;
    value.client = Number(this.clientId);

    if (this.editingBookIndex !== undefined) {
      const bookGroup = this.books.at(this.editingBookIndex);
      bookGroup.patchValue(value);
      await this.clientsService.saveClientBook({ ...bookGroup.value, id: value.id, bookDate: toDateOnlyPayload(bookGroup.value.bookDate) });
    } else {
      await this.clientsService.saveClientBook({ ...value, bookDate: toDateOnlyPayload(value.bookDate) });
    }
    await this.getData();
    this.closeBookModal();
  }

  openBookModal(index?: number) {
    this.submitError = '';
    // added logic to find the index in the form array based on the id from the table
    if (index !== undefined) {
      const itemFromTable = this.configBooksTable?.data?.[index];
      if (itemFromTable?.id !== undefined && itemFromTable?.id !== null) {
        const indexInForm = this.books.controls.findIndex(ctrl => ctrl.value?.id === itemFromTable.id);
        if (indexInForm >= 0) index = indexInForm;
      }
    }

    this.editingBookIndex = index;
    // El modo histórico deshabilita el formulario; al abrirlo de nuevo debe volver a ser editable.
    this.bookForm.enable({ emitEvent: false });
    if (index !== undefined) {
      const book = this.snapshotData
        ? this.configBooksTable.data[index]
        : this.books.at(index).value;
      this.bookForm.patchValue(book);
    } else {
      this.bookForm.reset();
    }
    this.isBookModalVisible = true;
    if (this.snapshotData) {
      this.bookForm.disable();
    }
  }

  closeBookModal() {
    this.submitError = '';
    this.isBookModalVisible = false;
    this.editingBookIndex = undefined;
    this.bookForm.reset();
  }

  async confirmDeleteBook() {
    this.submitError = '';
    try {
      await this.doConfirmDeleteBook();
    } catch (error) {
      console.error(error);
      this.submitError = 'No se pudo eliminar. Inténtalo de nuevo.';
    }
  }

  private async doConfirmDeleteBook() {
    const book = this.books.at(this.deleteBookIndex).value;
    if (book.id) {
      await this.clientsService.deleteClientBook(book.id);
      await this.getData();
    }
    this.deleteBookIndex = undefined;
    this.isDeleteBookModalVisible = false;
  }

  async openDeleteBookModal(index: number) {
    this.submitError = '';
    //find the index in the form array based on the id from the table
    const itemFromTable = this.configBooksTable?.data?.[index];
    if (itemFromTable?.id !== undefined && itemFromTable?.id !== null) {
      const indexInForm = this.books.controls.findIndex(ctrl => ctrl.value?.id === itemFromTable.id);
      if (indexInForm >= 0) index = indexInForm;
    }

    this.deleteBookIndex = index;
    this.isDeleteBookModalVisible = true;
  }

  closeDeleteBookModal() {
    this.submitError = '';
    this.deleteBookIndex = undefined;
    this.isDeleteBookModalVisible = false;
  }
}

